import React, { useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import {
  Card,
  CardContent,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Backdrop,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import {
  useGetTotalSalesQuery,
  useGetDailySalesQuery,
  useGetWeeklySalesQuery,
  useGetMonthlySalesQuery,
  useGetPendingSalesQuery,
} from "../../redux/Slices/reportApi";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Button from "components/styled-component/ReusableButton";
import { useMaterialUIController, setDirection } from "context";
import MDBox from "components/MDBox";
        import { ResponsiveAreaBump } from '@nivo/bump'

function SalesReport() {
  const [controller, dispatch] = useMaterialUIController();
  const componentRef = useRef();
  const chartData = [
    {
      id: "المبيعات اليومية",
      data: [
        { x: "يناير", y: 25 },
        { x: "فبراير", y: 30 },
        { x: "مارس", y: 22 },
        { x: "أبريل", y: 28 },
        { x: "مايو", y: 35 },
        { x: "يونيو", y: 40 },
      ],
    },
    {
      id: "المبيعات الأسبوعية",
      data: [
        { x: "يناير", y: 120 },
        { x: "فبراير", y: 150 },
        { x: "مارس", y: 180 },
        { x: "أبريل", y: 140 },
        { x: "مايو", y: 200 },
        { x: "يونيو", y: 220 },
      ],
    },
    {
      id: "المبيعات الشهرية",
      data: [
        { x: "يناير", y: 500 },
        { x: "فبراير", y: 620 },
        { x: "مارس", y: 580 },
        { x: "أبريل", y: 700 },
        { x: "مايو", y: 750 },
        { x: "يونيو", y: 800 },
      ],
    },
    {
      id: "الطلبات المعلقة",
      data: [
        { x: "يناير", y: 15 },
        { x: "فبراير", y: 12 },
        { x: "مارس", y: 18 },
        { x: "أبريل", y: 10 },
        { x: "مايو", y: 8 },
        { x: "يونيو", y: 5 },
      ],
    },
  ];
  // Set RTL direction like other pages
  useEffect(() => {
    setDirection(dispatch, "rtl");
  }, [dispatch]);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Sales Reports",
    pageStyle: `
      @page {
        size: A4;
        margin: 20mm;
      }
      @media print {
        body {
          color: black;
          background: white;
          direction: rtl;
        }
        .print-hidden, .MuiBox-root, .MuiAppBar-root, .MuiDrawer-root {
          display: none !important;
        }
        .MuiPaper-root {
          box-shadow: none !important;
          border: none !important;
        }
      }
    `,
    onPrintError: (errorLocation, error) => {
      console.error("Print error:", errorLocation, error);
    },
  });

  // Wrap handlePrint to check ref
  const onPrintClick = () => {
    if (!componentRef.current) {
      console.error("Cannot print: componentRef is not ready");
      return;
    }
    handlePrint();
  };

  // Update Button
  // creating sales report
  const {
    data: successfulData,
    isLoading: successfulLoading,
    error: successfulError,
  } = useGetTotalSalesQuery();
  const { data: dailyData, isLoading: dailyLoading, error: dailyError } = useGetDailySalesQuery();
  const {
    data: weeklyData,
    isLoading: weeklyLoading,
    error: weeklyError,
  } = useGetWeeklySalesQuery();
  const {
    data: monthlyData,
    isLoading: monthlyLoading,
    error: monthlyError,
  } = useGetMonthlySalesQuery();
  const {
    data: pendingData,
    isLoading: pendingLoading,
    error: pendingError,
  } = useGetPendingSalesQuery();

  const isLoading =
    successfulLoading || dailyLoading || weeklyLoading || monthlyLoading || pendingLoading;

  if (isLoading) {
    return (
      <DashboardLayout>
        <Backdrop open={isLoading} sx={{ zIndex: 2000, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </DashboardLayout>
    );
  }

  return (
    <MDBox>
      <MDBox elevation={3} sx={{ p: 4, maxWidth: 1200, mx: "auto", bgcolor: "transparent" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" component="h1" color="text">
            تقارير المبيعات
          </Typography>
          <Button variant="primary" size="medium" onClick={onPrintClick} className="print-hidden">
            <PrintIcon style={{ marginLeft: "0.5rem", color: "white" }} />
            طباعة التقرير
          </Button>
        </Box>

        <div ref={componentRef}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    إجمالي الطلبات الناجحة
                  </Typography>
                  <Typography variant="h4">{successfulData?.data || "N/A"}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    الطلبات اليومية
                  </Typography>
                  <Typography variant="h4">{dailyData?.data || "N/A"}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    الطلبات الأسبوعية
                  </Typography>
                  <Typography variant="h4">{weeklyData?.data || "N/A"}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    الطلبات الشهرية
                  </Typography>
                  <Typography variant="h4">{monthlyData?.data || "N/A"}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    الطلبات المعلقة
                  </Typography>
                  <Typography variant="h4">{pendingData?.data || "N/A"}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Box mt={4} textAlign="center" className="print-hidden">
            <Typography variant="caption" color="textSecondary">
              Generated on {new Date().toLocaleDateString()}
            </Typography>
          </Box>
        </div>
      </MDBox>

      <Box
        height="500px"
        border="1px solid #ccc"
        borderRadius="4px"
        mt="40px"
        p={2}
      >
        <Typography variant="h6" mb={2}>
          تحليل المبيعات
        </Typography>
        <Box height="400px">
          <ResponsiveAreaBump
            data={chartData}
            margin={{ top: 40, right: 150, bottom: 40, left: 150 }}
            spacing={8}
            colors={{ scheme: "nivo" }}
            blendMode="multiply"
            defs={[
              {
                id: "dots",
                type: "patternDots",
                background: "inherit",
                color: "rgba(255, 255, 255, 0.3)",
                
                size: 4,
                padding: 1,
                stagger: true,
              },
            ]}
            fill={[{ match: "*", id: "dots" }]}
            startLabel="id"
            endLabel="id"
            axisTop={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: "",
              legendPosition: "middle",
              legendOffset: -36,
            }}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: "",
              legendPosition: "middle",
              legendOffset: 32,
            }}
          />
        </Box>
      </Box>
      
    </MDBox>
  );
}

export default SalesReport;
